// components/nodes/CustomNode.jsx
import React, { memo, useCallback, useEffect, useState, useRef } from 'react';
import { useReactFlow, NodeResizer } from '@xyflow/react';
import CustomHandle from './CustomHandle';
import { FaBorderNone, FaServer, FaFile, FaFileVideo, FaVideo, FaCloud, FaPlayCircle,
  FaUser, FaSatellite, FaMobileAlt, FaDesktop, FaNetworkWired, FaMicrophone,
  FaTv, FaRobot, FaStream, FaUserCog, FaHeadset, FaCog, FaBullhorn, FaDatabase,
  FaAd, FaNewspaper, FaBroadcastTower, FaSignal, FaCloudUploadAlt, FaSatelliteDish, FaWifi, FaFilm, FaGlobe,
  FaTwitch, FaTiktok, FaFacebook, FaInstagram, FaYoutube, FaArrowDown, FaArrowLeft, FaArrowRight, FaArrowUp, FaEquals, FaFingerprint,
  FaApple, FaGoogle, FaHdd, FaTimes
} from 'react-icons/fa';
// import iconSrc from '../../utils/Uplynk_LogoType_WhiteText.svg';
import { useFlow } from '../../context/FlowContext/FlowContext';
import NodeOverlay from './featureOverlay/NodeOverlay';

import { ReactComponent as UplynkLogo } from '../../utils/Uplynk_LogoType_WhiteText.svg';
import { ReactComponent as ZixiLogo } from '../../utils/ZixiLogo.svg';
import { ReactComponent as BitmovinLogo } from '../../utils/BitmovinLogo.svg';
import { ReactComponent as GeminiLogo } from '../../utils/GeminiLogo.svg';
import { ReactComponent as GoogleColorLogo } from '../../utils/GoogleColorLogo.svg';
import { ReactComponent as OracleLogo } from '../../utils/OracleLogo.svg';
import { ReactComponent as AkamaiLogo } from '../../utils/AkamaiLogo.svg';
import { ReactComponent as Security } from '../../utils/Security.svg';
import { ReactComponent as ControlRoom } from '../../utils/ControlRoom.svg';
import { ReactComponent as Cycle } from '../../utils/Cycle.svg';

const CustomNode = ({ id, data, selected }) => {
  const { isLocked, addHistoryEvent, showAllModuleOverlays } = useFlow();
  const { style = {}, shape } = data;
  const { getNode, setNodes } = useReactFlow();

  // Refs for resize management
  const resizeStart = useRef(null);
  const resizeTimer = useRef(null);
  const [isResizing, setIsResizing] = useState(false);
  const [showOverlay, setShowOverlay] = useState(true);

  const defaultStyle = {
    backgroundColor: '#ffcc00',
    display: 'flex',
    borderWidth: '2px',
    borderStyle: 'solid',
    borderColor: '#333333',
    borderRadius: '10px',
    color: '#333',
    fontSize: '14px',
    fontWeight: 'bold',
    padding: '10px',
    textAlign: 'center',
    // textAlign: style.textAlign || 'center', // Swapped instead for a Vertical and Horizontal Slider
    alignItems: 'center', // New alignment property
  };

  // Convert composite border to individual properties if present
  const processedStyle = { ...style };

  // const onResizeStart = useCallback(() => {
  //   const node = getNode(id);
  //   if (!node) return;
    
  //   resizeStart.current = {
  //     position: { ...node.position }, // Create a new object to avoid reference issues
  //     width: node.width,
  //     height: node.height
  //   };
  // }, [id, getNode]);
  
  // const onResizeEnd = useCallback((evt, { width, height }) => {
  //   if (!resizeStart.current) return;
    
  //   const node = getNode(id);
  //   if (!node) return;
  
  //   // Only add history event if dimensions or position changed
  //   if (resizeStart.current.width !== width || 
  //       resizeStart.current.height !== height ||
  //       resizeStart.current.position.x !== node.position.x ||
  //       resizeStart.current.position.y !== node.position.y) {
  
  //     const resizeEvent = {
  //       type: 'resize',
  //       nodeID: id,
  //       source: {
  //         width: resizeStart.current.width,
  //         height: resizeStart.current.height,
  //         position: { ...resizeStart.current.position }
  //       },
  //       destination: {
  //         width,
  //         height,
  //         position: { ...node.position }
  //       }
  //     };
  //     addHistoryEvent(resizeEvent);
  //   }
  //   resizeStart.current = null;
  // }, [id, getNode, addHistoryEvent]);
  
 // Hide overlay during resize to improve performance
 useEffect(() => {
  // console.log("isResizing", isResizing);
  if (isResizing) {
    setShowOverlay(false);
  } else {
    // Delay showing overlay until after resize complete + paint
    const timer = setTimeout(() => {
      setShowOverlay(true);
    }, 100);
    return () => clearTimeout(timer);
  }
}, [isResizing]);

// Cleanup timers on unmount
useEffect(() => {
  return () => {
    if (resizeTimer.current) {
      clearTimeout(resizeTimer.current);
    }
  };
}, []);

// Optimized resize handlers
const onResizeStart = useCallback(() => {
  setIsResizing(true);
  
  // Store initial state
  const node = getNode(id);
  if (!node) return;
  
  resizeStart.current = {
    position: { ...node.position },
    width: node.width,
    height: node.height
  };
}, [id, getNode]);

// This is the key handler that debounces properly
const onResizeEnd = useCallback((evt, params) => {
  // Clear any pending timers first
  if (resizeTimer.current) {
    clearTimeout(resizeTimer.current);
  }
  
  // Now handle the resize completion
  setIsResizing(false);
 
  // Delay the resize completion to avoid cascading resize effects
  resizeTimer.current = setTimeout(() => {
    // const { width, height } = params;
    
    if (!resizeStart.current) return;
    
    const node = getNode(id);
    if (!node) return;
  
    // Only add history event if dimensions or position changed
    if (resizeStart.current.width !== params.width || 
        resizeStart.current.height !== params.height ||
        resizeStart.current.position.x !== node.position.x ||
        resizeStart.current.position.y !== node.position.y) {
  
      addHistoryEvent({
        type: 'resize',
        nodeID: id,
        source: {
          width: resizeStart.current.width,
          height: resizeStart.current.height,
          position: { ...resizeStart.current.position }
        },
        destination: {
          width: params.width,
          height: params.height,
          position: { ...node.position }
        }
      });
    }
    resizeStart.current = null;
  }, 100); // Longer timeout to ensure browser has repainted
}, [id, getNode, addHistoryEvent]);

  // Merge default styles with processed custom styles
  const nodeStyles = { 
    ...defaultStyle, 
    ...processedStyle,
  };
  // console.log("nodeStyles", nodeStyles);

  let iconSize = parseInt(data?.iconSize) || 22;
  
  const getAlignmentStyles = () => ({
    display: 'flex',
    justifyContent: 'center',
    alignItems: data.style?.alignItems || 'center',
    // gap: data.style?.gap || (data.label ? '8px' : '0'),
    width: '100%',
    height: '100%',  // Add this to ensure full height
  });


  // Icon handling - keeping your existing switch statement
  let icon = null;
  switch (data.icon) {
    case 'server':
      icon = <FaServer size={iconSize} />;
      break;
    case 'liveVideo':
      icon = <FaVideo size={iconSize} />;
      break;
    case 'file':
      icon = <FaFile size={iconSize} />;
      break;
    case 'camera':
      icon = <FaFilm size={iconSize} />;
      break;
    case 'fileVideo':
      icon = <FaFileVideo size={iconSize} />;
      break;
    case 'cloud':
      icon = <FaCloud size={iconSize} />;
      break;
    case 'videoPlayer':
      icon = <FaPlayCircle size={iconSize} />;
      break;
    case 'person':
      icon = <FaUser size={iconSize} />;
      break;
    case 'satellite':
      icon = <FaSatellite size={iconSize} />;
      break;
    case 'mobilePhone':
      icon = <FaMobileAlt size={iconSize} />;
      break;
    case 'stb':
      icon = <FaHdd size={iconSize} />;
      break;
    case 'desktop':
      icon = <FaDesktop size={iconSize} />;
      break;
    case 'ip':
      icon = <FaNetworkWired size={iconSize} />;
      break;
    case 'microphone':
      icon = <FaMicrophone size={iconSize} />;
      break;
    case 'tv':
      icon = <FaTv size={iconSize} />;
      break;
    case 'ai':
      icon = <FaRobot size={iconSize} />;
      break;
    case 'streaming':
      icon = <FaStream size={iconSize} />;
      break;
    case 'operatorCog':
      icon = <FaUserCog size={iconSize} />;
      break;
    case 'operatorHeadset':
      icon = <FaHeadset size={iconSize} />;
      break;
    case 'encoder':
      icon = <FaCog size={iconSize} />;
      break;
    case 'cycle':
      icon = <Cycle width={iconSize} height={iconSize} />;
      break;
    case 'controlroom':
      icon = <ControlRoom width={iconSize} height={iconSize} />;
      break;
    case 'security':
      icon = <Security width={iconSize} height={iconSize} />;
      break;
    case 'bullhorn':
      icon = <FaBullhorn size={iconSize} />;
      break;
    case 'ads':
      icon = <FaAd size={iconSize} />;
      break;
    case 'dataCentre':
      icon = <FaDatabase size={iconSize} />;
      break;
    case 'borderNone':
      icon = <FaBorderNone size={iconSize} />;
      break;
    case 'newspaper':
      icon = <FaNewspaper size={iconSize} />;
      break;
    case 'broadcastTower':
      icon = <FaBroadcastTower size={iconSize} />;
      break;
    case 'signal':
      icon = <FaSignal size={iconSize} />;
      break;
    case 'cloudUpload':
      icon = <FaCloudUploadAlt size={iconSize} />;
      break;
    case 'satelliteDish':
      icon = <FaSatelliteDish size={iconSize} />;
      break;
    case 'wifi':
      icon = <FaWifi size={iconSize} />;
      break;
    case 'globe':
      icon = <FaGlobe size={iconSize} />;
      break;
    case 'apple':
      icon = <FaApple size={iconSize} />;
      break;
    case 'google':
      icon = <FaGoogle size={iconSize} />;
      break;
    case 'twitch':
      icon = <FaTwitch size={iconSize} />;
      break;
    case 'tiktok':
      icon = <FaTiktok size={iconSize} />;
      break;
    case 'facebook':
      icon = <FaFacebook size={iconSize} />;
      break;
    case 'instagram':
      icon = <FaInstagram size={iconSize} />;
      break;
    case 'youtube':
      icon = <FaYoutube size={iconSize} />;
      break;
    case 'arrowDown':
      icon = <FaArrowDown size={iconSize} />;
      break;
    case 'arrowLeft':
      icon = <FaArrowLeft size={iconSize} />;
      break;
    case 'arrowRight':
      icon = <FaArrowRight size={iconSize} />;
      break;
    case 'arrowUp':
      icon = <FaArrowUp size={iconSize} />;
      break;
    case 'equals':
      icon = <FaEquals size={iconSize} />;
      break;
    case 'fingerprint':
      icon = <FaFingerprint size={iconSize} />;
      break;
    case 'uplynk':
      icon = <UplynkLogo width={iconSize * 2} height={iconSize * 1} />;
      break;
    case 'zixi':
      icon = <ZixiLogo width={iconSize * 2} height={iconSize * 1} />;
      break;
    case 'bitmovin':
      icon = <BitmovinLogo width={iconSize * 3} />;
      break;
    case 'gemini':
      icon = <GeminiLogo width={iconSize * 1} height={iconSize * 1} />;
      break;
    case 'googlecolor':
      icon = <GoogleColorLogo width={iconSize * 1} height={iconSize * 1} />;
      break;
    case 'oracle':
      icon = <OracleLogo width={iconSize * 3} />;
      break;
    case 'akamai':
      icon = <AkamaiLogo width={iconSize * 3} />;
      break;
    case 'x':
      // icon = <FaTimes width={iconSize * 2} height={iconSize * 2} />;
      icon = <FaTimes size={iconSize} />;
      break;
    case 'none':
      icon = null;
      break;
    default:
      break;
  }

  return (
    <>
      {/* {data.resizeable !== false && ( */}
      {/* {!isLocked && selected && (
        <NodeResizer 
          minWidth={20}
          minHeight={20}
          isVisible={selected}
          onResizeStart={onResizeStart}
          onResizeEnd={onResizeEnd}
        
          lineStyle={{
            borderWidth: '0px',
            borderStyle: 'solid',
            borderColor: style.borderColor,
            zIndex:'1000',
          }}
          handleStyle={{
            width: '6px',
            height: '6px',
            backgroundColor: 'white',
            borderWidth: '2px',
            borderStyle: 'solid',
            borderColor: '#60A5FA',
            zIndex: '999',
          }}
        />
      )} */}
      {!isLocked && selected && (
        <NodeResizer 
          minWidth={20}
          minHeight={20}
          isVisible={selected}
          onResizeStart={onResizeStart}
          onResizeEnd={onResizeEnd}
          lineStyle={{
            borderWidth: '0px',
            borderStyle: 'solid',
            borderColor: style.borderColor,
            zIndex: '1000',
          }}
          handleStyle={{
            width: '6px',
            height: '6px',
            backgroundColor: 'white',
            borderWidth: '2px',
            borderStyle: 'solid',
            borderColor: '#60A5FA',
            zIndex: '999',
          }}
        />
      )}
      <div className={isResizing ? 'resizing-node' : ''}
        style={{
          ...nodeStyles,
          height: '100%',
          width: '100%',
          borderStyle: data.style.borderColor === null ? 'none' : data.style.borderStyle || 'solid',
          borderWidth: data.style.borderColor === null ? '0px' : data.style.borderWidth || '1px',
          borderColor: data.style.borderColor === null ? 'transparent' : data.style.borderColor,      
          boxShadow: selected ? isLocked  ? '0 0 20px 1px rgba(255, 255, 255, 0.3)' : '0 0 10px 1px rgba(255, 255, 255, 0.3)' :  '',
          // Add these lines for hardware acceleration during resize
          ...(isResizing ? {
            willChange: 'transform',
            transform: 'translateZ(0)',
            backfaceVisibility: 'hidden'
          } : {})
      }}>
        {data.feature && !isResizing && (
        // {data.feature && (
          <NodeOverlay 
            data={data} 
            id={id} 
            isLocked={isLocked} 
            selected={selected} 
            showAllModuleOverlays={showAllModuleOverlays}
            style={{
              opacity: isResizing ? 0 : 1,
              transition: 'opacity 100ms ease-in-out',
              pointerEvents: isResizing ? 'none' : 'auto'
            }}
            hidden={!(data.feature && showAllModuleOverlays && selected || data.feature && isLocked && (showAllModuleOverlays || selected))}
          />
        )}
        <div className='' style={{
          ...getAlignmentStyles(),
          flexDirection: data?.style?.flexDirection || 'row',
        }}>
          <div className={`flex items-center ${data.style.flexDirection === 'column' ? 'flex-col' : 'flex-row' }`}
            style={{
              gap: data.iconSpacing || (data.label ? '8px' : '0'),
              marginTop: `${data?.offsetV ?? 0}px`,
              marginLeft: `${data?.offsetH ?? 0}px`,
            }}
          >
            {icon}
            <div className=''>
              <div className={`whitespace-pre leading-6`} // Added flex container
                style={{
                  marginBottom: `${data?.titleDescSpacing ?? 0}em`,
                }}
                >
                  {data.label}
              </div>
              <div className={`whitespace-pre font-normal top-0 text-[0.7em] pt-0 ${data.label ? 'mt-0' : '-mt-0.5' }`} // Added flex container
                style={{
                  lineHeight: `${data?.descSpacing || 1.3}`,
                  display: 'block',
                  marginTop: `-${
                    (data?.descSpacing || 1.3) > 1
                      ? (data?.descSpacing - 1) * 0.9 * 0.6 
                      : 0
                  }em`,
                  color: data.descFontColor || data.style.color,
                  fontSize: `${data.descFontSize}px`
                }}
              >
                  {data.description}
              </div>
            </div>
          </div>
        </div>

        {data.sourcePosition && (
          <CustomHandle type="source" position={data.sourcePosition} />
        )}
        {data.targetPosition && (
          <CustomHandle type="target" position={data.targetPosition} />
        )}

      </div>
    </>
  );

};

export default memo(CustomNode);